import React from "react";
import { Link } from "react-router-dom";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-bg text-white grain">
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-bg/75 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="relative w-9 h-9 flex items-center justify-center">
              <div className="absolute inset-0 bg-gold-grad rounded-sm rotate-45" />
              <span className="relative font-display text-bg text-xl">Z</span>
            </div>
            <div className="leading-none">
              <div className="font-display text-lg tracking-tight">ZIP CITY</div>
              <div className="font-serif-accent text-gold text-[11px]">Las Vegas</div>
            </div>
          </Link>
          <Link to="/" className="font-display uppercase text-[11px] tracking-[0.22em] text-muted hover:text-gold">← Home</Link>
        </div>
      </header>
      <article className="max-w-3xl mx-auto px-6 py-12 prose prose-invert">
        <div className="font-serif-accent text-gold text-lg mb-2">~ Legal</div>
        <h1 className="font-display uppercase text-4xl lg:text-5xl tracking-tight mb-4">{title}</h1>
        <div className="text-muted text-sm font-serif-accent mb-10">Last updated: April 30, 2026</div>
        <div className="space-y-5 text-sm leading-relaxed text-muted">{children}</div>
      </article>
    </div>
  );
}

export function TermsPage() {
  return (
    <Shell title="Terms of Service">
      <p>By accessing Zip City Las Vegas ("Zip City", "we", "us"), you agree to these terms. If you do not agree, do not use the site.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">1. Service description</h2>
      <p>Zip City is an information directory of Las Vegas businesses, deals, events, and entertainment. We do not own, operate, or guarantee any business listed. All offers, prices, and availability are determined by the listing business.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">2. Adult content (21+)</h2>
      <p>Certain categories — including Gentlemen's Clubs, Adult Entertainment, Brothels, and Cannabis Dispensaries — contain content for users 21 years of age and older. By entering those areas, you affirm you are at least 21. Nevada law strictly prohibits the consumption of cannabis in public; legal brothels are located outside Clark County.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">3. Business accounts & subscriptions</h2>
      <ul className="list-disc list-inside space-y-1">
        <li>Subscriptions are billed monthly through Stripe. Founding-member rates are locked for as long as the subscription remains in good standing.</li>
        <li>Cancel any time via Manage Billing in your portal — access continues through the end of the paid period.</li>
        <li>You must own or be authorized to represent the business you list. A valid Nevada business license is required.</li>
        <li>Pop-up ads, banner ads, and promoted spotlights are billed as one-time purchases for the disclosed run-length.</li>
      </ul>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">4. Affiliate program</h2>
      <p>Affiliates earn 20% recurring commission on paid invoices from accounts they refer. Referrals must be unique paying customers. Self-referrals, fraudulent activity, or chargebacks void commission. Payouts are issued at our discretion via methods we communicate.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">5. Acceptable use</h2>
      <p>You agree not to scrape, attack, or misrepresent listings. We may remove content or accounts that violate these terms or applicable Nevada/US law.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">6. Limitation of liability</h2>
      <p>Zip City is provided as-is. We are not responsible for the content, services, or actions of any business listed. Use the directory at your own discretion.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">7. Changes</h2>
      <p>We may update these terms; continued use after changes constitutes acceptance.</p>
      <p className="mt-10">Questions: <span className="text-gold">hello@zipcityvegas.com</span></p>
    </Shell>
  );
}

export function PrivacyPage() {
  return (
    <Shell title="Privacy Policy">
      <p>This policy explains what we collect and how we use it. We keep things simple: we collect only what we need to run Zip City and we never sell your data.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">What we collect</h2>
      <ul className="list-disc list-inside space-y-1">
        <li><strong>Newsletter signups:</strong> email address only.</li>
        <li><strong>Business accounts:</strong> business name, license number, address, contact email/phone, owner identifier.</li>
        <li><strong>Payments:</strong> processed by Stripe. We never see or store your card data.</li>
        <li><strong>Anonymous browsing:</strong> a random ID stored in your browser localStorage (`zc_user_id`) so we can attach a claim or subscription to you without requiring an account.</li>
        <li><strong>Cookies:</strong> minimal — used only to persist your local user ID, referral codes, and dismissed-popup state.</li>
      </ul>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">What we don't do</h2>
      <ul className="list-disc list-inside space-y-1">
        <li>Sell your personal data to third parties.</li>
        <li>Build advertising profiles based on your behavior.</li>
        <li>Track you across other websites.</li>
      </ul>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">Third parties we use</h2>
      <ul className="list-disc list-inside space-y-1">
        <li><strong>Stripe</strong> — payment processing. <a className="text-gold" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a></li>
        <li><strong>Cloudinary</strong> — image hosting for business uploads. <a className="text-gold" href="https://cloudinary.com/privacy" target="_blank" rel="noreferrer">cloudinary.com/privacy</a></li>
        <li><strong>MongoDB</strong> — database for listings + subscriber emails.</li>
      </ul>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">Your rights</h2>
      <p>If you're in the EU, UK, or California, you may request access, correction, or deletion of your personal data. Email <span className="text-gold">privacy@zipcityvegas.com</span> and we'll respond within 30 days.</p>
      <h2 className="font-display uppercase text-xl tracking-tight text-white mt-6">Changes</h2>
      <p>We may update this policy; the "last updated" date above will reflect changes.</p>
    </Shell>
  );
}
